"use client";

import { useEffect, useMemo, useState } from "react";

type Props = {
  idioma: "es" | "en";
  fecha: string;
  cambiarFecha: (fecha: string) => void;
  /** null = no se pudo consultar: el día queda neutro, no como agotado. */
  disponible: (fecha: string) => boolean | null | Promise<boolean | null>;
  hoy?: string;
  etiqueta?: string;
};

const isoHoy = () => new Date().toISOString().slice(0, 10);
const iso = (fecha: Date) => fecha.toISOString().slice(0, 10);
const fechaLocal = (fecha: string) => new Date(`${fecha}T12:00:00`);

export function CalendarioVisita({ idioma, fecha, cambiarFecha, disponible, hoy = isoHoy(), etiqueta }: Props) {
  const es = idioma === "es";
  const [mes, setMes] = useState(() => (fecha || hoy).slice(0, 7));
  const dias = useMemo(() => {
    const inicio = fechaLocal(`${mes}-01`);
    inicio.setDate(inicio.getDate() - ((inicio.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => { const dia = new Date(inicio); dia.setDate(inicio.getDate() + i); return iso(dia); });
  }, [mes]);
  const [estado, setEstado] = useState<Record<string, boolean | null>>({});
  // Abierto mientras se elige; al tocar un día se cierra y queda solo la fecha (un toque en «Cambiar» lo vuelve a abrir).
  const [abierto, setAbierto] = useState(true);

  useEffect(() => {
    let activa = true;
    setEstado({});
    const cargar = async () => {
      const resultado: (readonly [string, boolean | null])[] = [];
      for (let i = 0; i < dias.length; i += 6) {
        const grupo = await Promise.all(dias.slice(i, i + 6).map(async (dia) => [dia, dia >= hoy ? await disponible(dia) : false] as const));
        resultado.push(...grupo);
      }
      if (activa) setEstado(Object.fromEntries(resultado));
    };
    cargar().catch(() => { if (activa) setEstado(Object.fromEntries(dias.map((dia) => [dia, null]))); });
    return () => { activa = false; };
  }, [dias, disponible, hoy]);

  const titulo = fechaLocal(`${mes}-01`).toLocaleDateString(es ? "es-MX" : "en-US", { month: "long", year: "numeric" });
  const anterior = () => { const d = fechaLocal(`${mes}-01`); d.setMonth(d.getMonth() - 1); setMes(iso(d).slice(0, 7)); };
  const siguiente = () => { const d = fechaLocal(`${mes}-01`); d.setMonth(d.getMonth() + 1); setMes(iso(d).slice(0, 7)); };
  const formato = (dia: string) => fechaLocal(dia).toLocaleDateString(es ? "es-MX" : "en-US", { weekday: "long", day: "numeric", month: "long" });
  const puedeRetroceder = mes > hoy.slice(0, 7);

  if (!abierto && fecha) return <div className="cal-compacto" role="group" aria-label={etiqueta ?? (es ? "Fecha elegida" : "Chosen date")}>
    <span className="cal-compacto-icono" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16v14H4zM4 10h16M8 3v4M16 3v4" /></svg></span>
    <b>{formato(fecha).replace(/^./, (c) => c.toUpperCase())}</b>
    <button type="button" onClick={() => setAbierto(true)}>{es ? "Cambiar" : "Change"}</button>
  </div>;
  return <div className="cal-visita" role="group" aria-label={etiqueta ?? (es ? "Disponibilidad por día" : "Availability by day")}>
    <div className="cal-visita-cabecera"><button type="button" onClick={anterior} disabled={!puedeRetroceder} aria-label={es ? "Mes anterior" : "Previous month"}>‹</button><strong>{titulo}</strong><button type="button" onClick={siguiente} aria-label={es ? "Mes siguiente" : "Next month"}>›</button></div>
    <div className="cal-visita-semana">{(es ? ["L", "M", "M", "J", "V", "S", "D"] : ["M", "T", "W", "T", "F", "S", "S"]).map((dia, i) => <span key={`${dia}-${i}`}>{dia}</span>)}</div>
    <div className="cal-visita-dias">{dias.map((dia) => {
      const fuera = !dia.startsWith(mes);
      const cargando = estado[dia] === undefined;
      const desconocido = estado[dia] === null || dia < hoy;
      const libre = estado[dia] === true && dia >= hoy;
      return <button key={dia} type="button" disabled={!libre} className={`${fuera ? "fuera" : ""}${fecha === dia ? " elegido" : ""}${!cargando && !libre && !desconocido ? " no" : ""}`} aria-pressed={fecha === dia} aria-label={`${formato(dia)}: ${cargando ? (es ? "consultando" : "checking") : desconocido ? (es ? "sin datos" : "no data") : libre ? (es ? "disponible" : "available") : (es ? "sin disponibilidad" : "unavailable")}`} onClick={() => { cambiarFecha(dia); setAbierto(false); }}><b>{Number(dia.slice(-2))}</b><small>{cargando ? "…" : desconocido ? "–" : libre ? (es ? "Sí" : "Yes") : (es ? "No" : "No")}</small></button>;
    })}</div>
    {dias.some((dia) => dia >= hoy && typeof estado[dia] === "boolean") && <p><i className="libre" /> {es ? "Disponible" : "Available"}<i className="no" /> {es ? "Sin disponibilidad" : "Unavailable"}</p>}
  </div>;
}
